import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import '../styles/DashboardHeader.css';

const DashboardHeader = ({ toggleSidebar, title = 'Nomination Review Queue', subtitle = 'Review and approve athlete nominations' }) => {
  const navigate = useNavigate();
  const { updatePassword, updateAdminProfileImage, fetchAdminProfileImage } = useAuth();
  const [showLogoutDropdown, setShowLogoutDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profileImageUrl, setProfileImageUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Get admin user data from localStorage
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const adminName = adminUser.name || 'Admin';
  const adminEmail = adminUser.email || 'admin@legacystat.com';

  // Load profile image on mount
  useEffect(() => {
    const loadProfileImage = async () => {
      // First check localStorage
      const storedUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      if (storedUser.profile_image) {
        setProfileImageUrl(storedUser.profile_image);
        return;
      }
      // Fallback: fetch from DB
      if (storedUser.email && fetchAdminProfileImage) {
        const result = await fetchAdminProfileImage();
        if (result.success && result.url) {
          setProfileImageUrl(result.url);
          // Update localStorage so we don't need to fetch again
          storedUser.profile_image = result.url;
          localStorage.setItem('adminUser', JSON.stringify(storedUser));
        }
      }
    };
    loadProfileImage();
  }, [fetchAdminProfileImage]);

  // Handle file selection for profile photo
  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPhoto(true);
    try {
      const result = await updateAdminProfileImage(file);
      if (result.success) {
        // Re-fetch from DB to confirm the real saved data
        const freshData = await fetchAdminProfileImage();
        if (freshData.success && freshData.url) {
          setProfileImageUrl(freshData.url);
          // Sync localStorage with DB truth
          const storedUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
          storedUser.profile_image = freshData.url;
          localStorage.setItem('adminUser', JSON.stringify(storedUser));
        } else {
          // Fallback to upload result if refetch fails
          setProfileImageUrl(result.url);
        }
      } else {
        console.error('Failed to upload profile image:', result.error);
      }
    } catch (err) {
      console.error('Profile image upload error:', err);
    } finally {
      setUploadingPhoto(false);
      // Reset file input so the same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleChangePhotoClick = () => {
    setShowLogoutDropdown(false);
    fileInputRef.current?.click();
  };

  // Get two-letter initials from admin name (e.g. "Legacy Admin" → "LA")
  const getInitials = (name) => {
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return (name.charAt(0) + (name.charAt(1) || '')).toUpperCase();
  };

  // Render avatar (uploaded image or letter avatar) with verified badge
  const renderAvatar = (size = 40, className = '') => {
    const badgeSize = size * 0.4;
    const badge = (
      <svg className="avatar-verified-badge" width={badgeSize} height={badgeSize} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" fill="#D8A134" />
        <path d="M9 12l2 2 4-4" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );

    if (profileImageUrl) {
      return (
        <div className={`avatar-wrapper ${className}`} style={{ width: size, height: size }}>
          <img
            src={profileImageUrl}
            alt="Profile"
            className="dashboard-header-profile-avatar"
            style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }}
          />
          {badge}
        </div>
      );
    }
    const initials = getInitials(adminName);
    return (
      <div className={`avatar-wrapper ${className}`} style={{ width: size, height: size }}>
        <div
          className="letter-avatar"
          style={{ width: size, height: size, fontSize: size * 0.35 }}
        >
          {initials}
        </div>
        {badge}
      </div>
    );
  };

  const handleLogout = () => {
    // Clear all admin authentication data
    localStorage.removeItem('adminToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('adminUser');
    
    // Clear any other dashboard-related data
    localStorage.removeItem('dashboardPreferences');
    localStorage.removeItem('lastViewed');
    
    // Redirect to admin page
    navigate('/admin', { replace: true });
  };

  const handleProfileClick = () => {
    setShowLogoutDropdown(!showLogoutDropdown);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutDropdown(false);
    handleLogout();
  };

  const handleChangePasswordClick = () => {
    setShowLogoutDropdown(false);
    setShowPasswordModal(true);
    setError('');
    setSuccess('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
      const email = adminUser.email;

      if (!email) {
        setError('Unable to identify admin user');
        setLoading(false);
        return;
      }

      const result = await updatePassword(email, currentPassword, newPassword);

      if (result.success) {
        setSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setShowPasswordModal(false);
          setSuccess('');
        }, 2000);
      } else {
        setError(result.error || 'Failed to change password. Please check your current password.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!loading) {
      setShowPasswordModal(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    }
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLogoutDropdown && !event.target.closest('.dashboard-header-profile')) {
        setShowLogoutDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showLogoutDropdown]);

  return (
    <header className="dashboard-header">
      <div className="dashboard-header-container">
        {/* Mobile Menu Toggle */}
        <button className="dashboard-header-menu-toggle" onClick={toggleSidebar}>
          <img src="/assets/images/menu-icon.png" alt="Menu" />
        </button>

        {/* Page Title Section */}
        <div className="dashboard-header-title-section">
          <h1 className="dashboard-header-title">{title}</h1>
          <p className="dashboard-header-subtitle">{subtitle}</p>
        </div>

        {/* Notification Bell */}
        <NotificationBell />

        {/* Profile Section with Logout */}
        <div className="dashboard-header-profile">
          <div className="dashboard-header-profile-content" onClick={handleProfileClick}>
            {uploadingPhoto ? (
              <div className="letter-avatar upload-loading" style={{ width: 40, height: 40 }}>
                <div className="upload-spinner"></div>
              </div>
            ) : (
              renderAvatar(40)
            )}
            <div className="dashboard-header-profile-info">
              <div className="dashboard-header-profile-name-wrapper">
                <span className="dashboard-header-profile-name">{adminName}</span>
                <svg className="dropdown-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="dashboard-header-profile-email">{adminEmail}</span>
            </div>
          </div>

          {/* Logout Dropdown */}
          {showLogoutDropdown && (
            <div className="dashboard-header-logout-dropdown">
              <div className="logout-dropdown-header">
                <div className="logout-user-info">
                  <div className="logout-user-avatar">
                    {renderAvatar(40)}
                  </div>
                  <div className="logout-user-details">
                    <p className="logout-user-name">{adminName}</p>
                    <p className="logout-user-email">{adminEmail}</p>
                  </div>
                </div>
              </div>
              
              <div className="logout-dropdown-divider"></div>

              <button className="logout-dropdown-item change-photo-btn" onClick={handleChangePhotoClick}>
                <svg className="logout-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <span>{uploadingPhoto ? 'Uploading...' : 'Change Photo'}</span>
              </button>

              <button className="logout-dropdown-item" onClick={handleChangePasswordClick}>
                <svg className="logout-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
                <span>Change Password</span>
              </button>

              <button className="logout-dropdown-item logout-item" onClick={handleLogoutConfirm}>
                <svg className="logout-icon" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Logout</span>
              </button>
            </div>
          )}
        </div>

        {/* Change Password Modal */}
        {showPasswordModal && (
          <div className="password-modal-overlay" onClick={handleCloseModal}>
            <div className="password-modal" onClick={(e) => e.stopPropagation()}>
              <div className="password-modal-header">
                <h2 className="password-modal-title">Change Password</h2>
                <button className="password-modal-close" onClick={handleCloseModal} disabled={loading}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <form className="password-modal-form" onSubmit={handleChangePassword}>
                <div className="password-form-group">
                  <label className="password-form-label">Current Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      className="password-form-input"
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      tabIndex="-1"
                      disabled={loading}
                    >
                      {showCurrentPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9.88 9.88a3 3 0 104.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0112 5c7 0 10 7 10 7a13.16 13.16 0 01-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 002 12s3 7 10 7a9.74 9.74 0 005.39-1.61" />
                          <line x1="2" y1="2" x2="22" y2="22" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="password-form-group">
                  <label className="password-form-label">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      className="password-form-input"
                      placeholder="Enter new password (min 8 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      tabIndex="-1"
                      disabled={loading}
                    >
                      {showNewPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9.88 9.88a3 3 0 104.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0112 5c7 0 10 7 10 7a13.16 13.16 0 01-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 002 12s3 7 10 7a9.74 9.74 0 005.39-1.61" />
                          <line x1="2" y1="2" x2="22" y2="22" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div className="password-form-group">
                  <label className="password-form-label">Confirm New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      className="password-form-input"
                      placeholder="Re-enter new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      tabIndex="-1"
                      disabled={loading}
                    >
                      {showConfirmPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M9.88 9.88a3 3 0 104.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0112 5c7 0 10 7 10 7a13.16 13.16 0 01-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 002 12s3 7 10 7a9.74 9.74 0 005.39-1.61" />
                          <line x1="2" y1="2" x2="22" y2="22" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="password-modal-error">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}

                {success && (
                  <div className="password-modal-success">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    <span>{success}</span>
                  </div>
                )}

                <div className="password-modal-actions">
                  <button
                    type="button"
                    className="password-modal-cancel"
                    onClick={handleCloseModal}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="password-modal-submit"
                    disabled={loading}
                  >
                    {loading ? 'Changing...' : 'Change Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Hidden file input for profile photo upload */}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handlePhotoSelect}
        />

        {/* Mobile Logout Button (separate) */}
        <button className="dashboard-header-logout-btn-mobile" onClick={handleLogout}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    </header>
  );
};

export default DashboardHeader;